export const XMAX_MIN_RECOMMENDED_INPUT_SHORT_EDGE = 480 as const

export type XmaxNoticeCode = 'LOW_INPUT_RESOLUTION'

export type XmaxNoticeInputKind = 'camera' | 'image' | 'video' | 'stream'

export type XmaxLowInputResolutionNotice = {
  readonly code: 'LOW_INPUT_RESOLUTION'
  readonly message: string
  readonly details: {
    readonly inputKind: XmaxNoticeInputKind
    readonly width: number
    readonly height: number
    readonly shortEdge: number
    readonly minimumShortEdge: typeof XMAX_MIN_RECOMMENDED_INPUT_SHORT_EDGE
  }
}

export type XmaxSdkNotice = XmaxLowInputResolutionNotice

/** Non-fatal SDK notice callback. The notice never interrupts the active operation. */
export type XmaxNoticeNotifier = (notice: XmaxSdkNotice) => void

export function createLowInputResolutionNotice(input: {
  inputKind: XmaxNoticeInputKind
  width: number
  height: number
  message: string
}): XmaxLowInputResolutionNotice | null {
  const { inputKind, width, height, message } = input
  if (
    !Number.isFinite(width)
    || !Number.isFinite(height)
    || width <= 0
    || height <= 0
  ) {
    return null
  }

  const shortEdge = Math.min(width, height)
  if (shortEdge > XMAX_MIN_RECOMMENDED_INPUT_SHORT_EDGE) {
    return null
  }

  return Object.freeze({
    code: 'LOW_INPUT_RESOLUTION',
    message,
    details: Object.freeze({
      inputKind,
      width,
      height,
      shortEdge,
      minimumShortEdge: XMAX_MIN_RECOMMENDED_INPUT_SHORT_EDGE,
    }),
  })
}

/** Schedule a consumer notice without delaying or failing the SDK operation. */
export function notifyNotice(
  notice: XmaxSdkNotice,
  notifier: XmaxNoticeNotifier | undefined,
): void {
  if (!notifier) {
    return
  }

  const run = () => {
    try {
      notifier(notice)
    } catch {
      // Consumer notice handlers must never interrupt media connection or publishing.
    }
  }

  if (typeof queueMicrotask === 'function') {
    queueMicrotask(run)
    return
  }
  void Promise.resolve().then(run)
}
